import React, { useEffect, useState } from "react";
import { Alert, Space, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import { formatDate } from "../shared/money";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";
import { DownloadButton, useLabels, useRange } from "./receivablesUi";

/** RP-2 Rechnungsausgangsbuch: all outgoing documents in number order with the gap check. */
const AusgangsbuchTab = () => {
  const { t } = useTranslation("accounting");
  const labels = useLabels();
  const { params, picker } = useRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getAusgangsbuch(params)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [params]);

  const series = Array.isArray(data?.series) ? data.series : [];
  const broken = series.filter(s => !s.ok);
  const name = `Rechnungsausgangsbuch_${params.from}_${params.to}`;
  return (
    <div>
      <PageToolbar
        filters={picker}
        actions={
          <div className="acct-actions">
            <DownloadButton
              label={t("Download CSV")}
              promise={() => api.downloadAusgangsbuch(params, "csv")}
              filename={`${name}.csv`}
            />
            <DownloadButton
              label={t("Download PDF")}
              promise={() => api.downloadAusgangsbuch(params, "pdf")}
              filename={`${name}.pdf`}
            />
          </div>
        }
      />
      {data ? (
        broken.length ? (
          broken.map(s => (
            <Alert
              key={s.key}
              type={s.gaps.some(g => !g.known) || s.duplicates.length ? "error" : "warning"}
              showIcon
              style={{ marginBottom: 8 }}
              message={`${s.label}: ${s.gaps.length} ${t("missing numbers")}${
                s.duplicates.length ? `, ${s.duplicates.length} ${t("duplicate numbers")}` : ""
              }`}
              description={
                <div>
                  {s.gaps.map(g => (
                    <div key={g.n} className="acct-wrap">
                      <strong>{g.number}</strong>
                      {" — "}
                      {g.known
                        ? g.text
                        : t("No document stored for this number. Document the reason (GoBD).")}
                    </div>
                  ))}
                  {s.duplicates.map(d => (
                    <div key={d} className="acct-wrap">
                      <strong>{d}</strong> {t("is used twice")}
                    </div>
                  ))}
                </div>
              }
            />
          ))
        ) : (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 8 }}
            message={t("All number ranges are without gaps.")}
          />
        )
      ) : null}
      {series.length ? (
        <Space wrap style={{ marginBottom: 8 }}>
          {series.map(s => (
            <Tag key={s.key} color={s.ok ? "green" : "red"}>
              {`${s.label}: ${s.first || "–"} … ${s.last || "–"} (${s.count})`}
            </Tag>
          ))}
        </Space>
      ) : null}
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={{ pageSize: 100, hideOnSinglePage: true }}
        rowKey="id"
        dataSource={data?.rows || []}
        summary={() =>
          data ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{`${t("Total")} (${data.totals.count})`}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <Amount cents={data.totals.net} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="end">
                <Amount cents={data.totals.tax} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="end">
                <Amount cents={data.totals.gross} strong />
              </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        }
        columns={[
          { title: t("Number"), dataIndex: "number", width: 140 },
          { title: t("Type"), dataIndex: "type", render: v => labels.type[v] || v },
          { title: t("Date"), dataIndex: "issueDate", render: formatDate, width: 100 },
          { title: t("Customer"), dataIndex: "customer" },
          { title: t("Refers to"), dataIndex: "reference" },
          { title: t("Net"), dataIndex: "net", align: "end", render: v => <Amount cents={v} /> },
          { title: t("VAT"), dataIndex: "tax", align: "end", render: v => <Amount cents={v} /> },
          {
            title: t("Gross"),
            dataIndex: "gross",
            align: "end",
            render: v => <Amount cents={v} signed />
          }
        ]}
      />
    </div>
  );
};

export default AusgangsbuchTab;
